import { Fragment, useMemo, type FC } from 'react';
import { useAtomValue } from 'jotai';
import { Source } from 'react-map-gl';
import type { ColorByRule } from 'components/color-by-attribute-modal';
import { Layer, LayerBand, LAYER_ORDER } from 'components/map';
import { buildUwiColorExpr, uwiTileUrls } from 'components/map/uwi-tiles';
// Shared with pre-drill rather than copied: same rules plus resolved UWI map in, same
// paint inputs out.
import {
  buildColorByUwiColorMap,
  tileFingerprint,
} from 'pages/pre-drill-wellbore-workspace/utils';
import type { FilteredUwi } from 'types/pre-drill-workspace';
import { areaColorByUwiMapAtom, wellStatusColorsAtom } from './atoms';
import { areaWellColorMap } from './color-by';

interface AreaScopedWellsLayerProps {
  /** The rules; the enabled, map-targeting ones decide what is painted. */
  colorRules: ColorByRule[];
  // Every well the boundary catches, each with its status — the base coat the legend
  // paints. From the one `include_filtered_uwis` call the colour pickers also read.
  wells: FilteredUwi[];
  /** Whether wells are shown at all; the rail's own toggle owns this overlay too. */
  visible: boolean;
}

/** Matched wells are emphasised: thicker, near-opaque coloured line. */
const MATCHED_LINE_WIDTH = 4;
/** Mapbox `match` needs a default for a tiled UWI with no colour of its own. */
const DEFAULT_WELL_COLOR = '#0F62FE';

// Paints the area's wells over the base layer in the legend's status colours, or a
// rule's where one matches. Via the `uwis` tile path, as Type Curve does; no minzoom.
export const AreaScopedWellsLayer: FC<AreaScopedWellsLayerProps> = ({
  colorRules,
  wells,
  visible,
}) => {
  const colorByUwiMap = useAtomValue(areaColorByUwiMapAtom);
  const statusColors = useAtomValue(wellStatusColorsAtom);

  const uwiColorMap = useMemo(() => {
    if (!visible) {
      return {};
    }
    return areaWellColorMap({
      wells,
      statusColors,
      ruleColors: buildColorByUwiColorMap(colorRules, colorByUwiMap),
    });
  }, [visible, wells, statusColors, colorRules, colorByUwiMap]);

  const tiles = useMemo(
    () => uwiTileUrls(Object.keys(uwiColorMap)),
    [uwiColorMap]
  );

  const colorExpr = useMemo(
    () => buildUwiColorExpr(uwiColorMap, DEFAULT_WELL_COLOR),
    [uwiColorMap]
  );
  const fp = useMemo(() => tileFingerprint(tiles), [tiles]);

  if (tiles.length === 0) {
    return null;
  }

  return (
    <LayerBand band={LAYER_ORDER.areaWells}>
      {tiles.map((tileUrl, idx) => (
        <Fragment key={`area-scoped-wells-${fp}-${idx}`}>
          {/* Deviation-survey line, for wells that have one. */}
          <Source
            id={`area-scoped-wells-survey-${fp}-${idx}`}
            type="vector"
            tiles={[tileUrl]}
          >
            <Layer
              id={`area-scoped-wells-survey-line-${fp}-${idx}`}
              type="line"
              source-layer="hits"
              paint={{
                'line-color': colorExpr,
                'line-width': MATCHED_LINE_WIDTH,
                'line-opacity': 0.85,
              }}
            />
          </Source>

          {/* Stick fallback: surface → bottom hole, for wells with no survey. */}
          <Source
            id={`area-scoped-wells-stick-${fp}-${idx}`}
            type="vector"
            tiles={[tileUrl.replace('/survey/', '/stick/')]}
          >
            <Layer
              id={`area-scoped-wells-stick-line-${fp}-${idx}`}
              type="line"
              source-layer="hits"
              paint={{
                'line-color': colorExpr,
                'line-width': MATCHED_LINE_WIDTH,
                'line-opacity': 0.6,
                'line-dasharray': [1, 1],
              }}
            />
          </Source>
        </Fragment>
      ))}
    </LayerBand>
  );
};
